import { useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import referralBg from '../../assets/figma/referral-banner-bg.jpg';
import cardsBg from '../../assets/figma/cards-banner-bg.jpg';

type SlideId = 'referral' | 'cards';

interface Props {
  onReferral?: () => void;
  onCards?: () => void;
}

const SLIDES: SlideId[] = ['referral', 'cards'];
const ROTATE_MS = 3000;

/** Home promo carousel — referral + cards slides, auto-advance every 3s. */
export function ReferralBanner({ onReferral, onCards }: Props) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const slide = SLIDES[index]!;

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  const goNext = (e: MouseEvent) => {
    e.stopPropagation();
    setIndex((i) => (i + 1) % SLIDES.length);
  };

  const onClick = () => {
    if (slide === 'referral') onReferral?.();
    else onCards?.();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative block h-[96px] w-full overflow-hidden rounded-[24px] bg-[#001a3d] text-left shadow-card active:opacity-95"
    >
      {SLIDES.map((id, i) => (
        <img
          key={id}
          src={id === 'referral' ? referralBg : cardsBg}
          alt=""
          className={`pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-500 ${
            i === index ? 'opacity-100' : 'opacity-0'
          } ${
            id === 'referral'
              ? 'scale-[1.2] object-[68%_40%]'
              : 'scale-[1.15] object-[78%_45%]'
          }`}
          draggable={false}
        />
      ))}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 w-[48%] bg-gradient-to-r from-[#001a3d] from-30% via-[#001a3d]/75 to-transparent"
      />

      <div className="relative z-10 flex h-full max-w-[55%] flex-col justify-center gap-1 py-2 pl-4 pr-1">
        {slide === 'referral' ? (
          <>
            <p className="text-[14px] font-extrabold leading-[1.08] tracking-tight text-white">
              <span className="block whitespace-nowrap">{t('wallet.bannerLine1')}</span>
              <span className="block whitespace-nowrap">{t('wallet.bannerLine2')}</span>
              <span className="block whitespace-nowrap">
                {t('wallet.bannerLine3Prefix')}
                <span className="text-[#8AD0FF]">AsiaWallet</span>
              </span>
            </p>
            <p className="text-[11px] font-semibold leading-[1.25] text-white/90">
              {t('wallet.bannerSubtitle')}
            </p>
          </>
        ) : (
          <>
            <p className="text-[15px] font-extrabold leading-[1.15] tracking-tight text-white">
              {t('wallet.bannerCardsTitle')}
            </p>
            <p className="text-[12px] font-semibold leading-[1.25] text-white/90">
              {t('wallet.bannerCardsSubtitle')}
            </p>
          </>
        )}
      </div>

      <span
        role="presentation"
        onClick={goNext}
        className="absolute right-2.5 top-1/2 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white"
      >
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
          <path
            d="M5 3.5L8.5 7L5 10.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}
